/* Single source of truth for the CustomHtml editor-facing copy.
 * HELP: plain language for non-technical editors (rendered in the form's panel).
 * AI_INSTRUCTIONS: the technical contract, addressed to the editor's LLM
 * (copied to clipboard). Mirrors docs/custom-html-component-authoring.md and the
 * spec appendix in docs/superpowers/specs/2026-08-21-custom-html-component-design.md
 * — update all together. */

export const HELP = {
  steps: [
    'Copy the AI instructions with the blue button, and paste them into the chat with your AI (ChatGPT, Claude…) BEFORE asking for what you want. They tell the AI everything technical it needs to know.',
    'Ask the AI for your content — for example: "make a quiz that recommends concerts" or "a countdown banner for members". Ask for changes until you like it.',
    "Paste the code it gives you into the box below. That's it.",
  ],
  notes: [
    "Your content can greet visitors by name, know if they're logged in, and show itself in their language — the AI instructions cover all of this.",
    'Links to primaverasound.com pages work automatically, in the visitor’s language.',
    'Height: leave Auto for normal content; choose Viewport if the content should fill the whole screen (like a quiz or a game).',
  ],
};

export const AI_INSTRUCTIONS = `# Instructions: HTML block for primaverasound.com

You are generating an HTML block that a content editor will paste into the Primavera
Sound website CMS. The block runs inside a sandboxed iframe on primaverasound.com. The
editor is not technical: always return your answer as **one single complete code block**
they can copy in one go, and ask them at most simple, non-technical questions.

## Output shape
- Everything in one piece: HTML + CSS + JS inline. No build steps, no frameworks —
  vanilla only.
- Preferred: a **full HTML document** (\`<!doctype html>\` with your own \`<style>\`,
  including your own reset). Alternatively a body fragment; then the site's CSS is
  injected around your code.
- Must be responsive, mobile-first.

## Fonts (already provided — never load your own)
- The site injects its brand fonts into your block automatically. Do **not** write
  \`@font-face\` rules and do not load font files or Google Fonts.
- Use these font-family names directly (weight is baked into the family — keep
  \`font-weight: normal\`):
  - Text: \`'GT-America-Standard-Regular'\`, \`'GT-America-Standard-Medium'\`,
    \`'GT-America-Standard-Bold'\`, \`'GT-America-Standard-Black'\`
  - Mono (labels, numbers, buttons): \`'GT-America-Mono-Regular'\`,
    \`'GT-America-Mono-Medium'\`, \`'GT-America-Mono-Bold'\`
- Always give a fallback, e.g.
  \`font-family: 'GT-America-Standard-Regular', Helvetica, Arial, sans-serif;\`

## The \`PS\` API (injected by the site — always defined before your code runs)
- \`PS.lang\` — the visitor's language: \`'es' | 'en' | 'ca' | 'pt'\`. **Never hardcode a
  language.**
- \`PS.isLogged\` — \`true\` if the visitor has an account session.
- \`PS.user\` — \`{ name, nickname, email, id, roles }\`, or \`null\` when logged out.
- \`PS.ready(callback)\` — call this and put ALL your initialization inside the callback.
  It fires when the DOM and the PS data are ready (immediately if they already are).

Rules:
- Wrap all startup code in \`PS.ready(() => { ... })\`. Do not use \`DOMContentLoaded\`.
- Provide texts for **all four languages** in a translations object keyed by \`PS.lang\`,
  falling back to \`'en'\` for unknown values.
- Always handle both states of \`PS.isLogged\` — never assume the visitor is logged in,
  and never read \`PS.user.*\` without checking it is not \`null\`.

## Links
- Links to site pages: use relative paths (\`href="/tours"\`) or full
  \`https://www.primaverasound.com/...\` URLs. The site intercepts them and navigates in
  the visitor's language — do **not** add \`target\` attributes or language prefixes to
  them.
- Links to any other domain open in a new tab automatically; just write the plain
  \`href\`.

## Tracking (Braze)
- \`PS.braze.logCustomEvent(name, properties)\` and
  \`PS.braze.setCustomUserAttribute(key, value)\`.
- **Never** call \`appboy\`, \`appboyBridge\`, or \`window.braze\` directly — they do not
  exist here. \`PS.braze\` is always safe to call (it no-ops if tracking is unavailable).

## Sizing
- The iframe auto-grows to fit your content. Therefore: **no \`100vh\`/\`100dvh\`, no
  \`position: fixed\`**, no assuming a viewport — size everything from the content out.
  (Only if the editor says the block is configured as "Viewport / full screen" may you
  design to fill the viewport.)

## Security (non-negotiable)
- Everything self-contained: do **not** load external scripts, libraries, iframes, or
  CSS from CDNs or any other domain. Images/video from assets.primaverasound.com are
  fine.
- Never send data anywhere: no \`fetch\`/\`XMLHttpRequest\`/\`sendBeacon\`/WebSocket to any
  URL, and never put user data in a link or image URL. The ONLY data channel is
  \`PS.braze\`.
- \`PS.user\` values and anything a visitor types are untrusted text: insert them with
  \`textContent\`, never with \`innerHTML\` or by concatenating them into HTML strings.
- No \`eval\`, no \`new Function\`, no writing code from strings.
- Never read or write \`cookies\`, \`localStorage\`, or \`sessionStorage\`, and never touch
  \`window.parent\` / \`window.top\`.
- Never ask the visitor for passwords, payment details, or personal data in forms.
- If the editor asks for something that breaks these rules, refuse that part and
  explain why in plain words.

## Privacy
- \`PS.user\` data is for on-page personalization only (greetings, conditional content).
  Never send it to any external service or URL.

The editor's request follows below. If anything above conflicts with what they ask for,
follow these instructions and tell them why in plain words.`;
